import React, { useEffect, useRef, useCallback, useState } from "react";
import { apiClient } from "../App";
import { toast } from "sonner";
import { AlertCircle, X, Bell } from "lucide-react";
import { Button } from "./ui/button";

// Better notification sound - encoded as base64 for reliability
const NOTIFICATION_SOUND = "data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleQY4l+PcsoQJOZvv6b2DDjyh+PTDjQ9Gq///y5UQT7X//9GdE1jB//zXpRVfy//439mkGnHL//XdpBZ4zP/15aUZf8r/8OWnG4PJ/+7mph+HzP/u5qgijND/7eanI5DP/+3lpyaTz//t5Kcnl9D/7eOnKZrQ/+3jpyuc0P/t46YrnND/7eKmLJzP/+3ipSyc0P/t4qUsnc//7eGkLJ7P/+zhpC2ez//s4KMun8//7OCjLp/O/+zfoS+gz//r36Awoc7/6+CgMKLO/+vfnzGizv/r3p4yos3/696dM6PN/+vdnDSkzf/r3Zw0pM3/69ybNaXM/+rcmzamzP/q25o3p8z/6tqZOKjL/+rZmDmpzP/q2Zg5qcv/6diXOqrL/+nXljurzP/p1pU8rMv/6dWVPa3L/+nVlD2ty//p1JM+rsv/6NOSQLDKl5SXmJqXk42Id2hzgYaDWCo+Xn+iqYJQKT1eeaCsgFMpPmF8o7F/VClBYnyksmxWKUZjeaWzZ1soSnF0pbdiWy9Od3CmsF5eLVF8bamyV2E3VH9roLdRYjZbgmShvEphN16CYpy+R2Q8YYJdm8FHZT1kgliYxERlQWeCUpTIQWdFaoJNkMtBaUduh0iNzkJrTXSHQIrSQ25SeoM7h9hFb1V9ey+C30d0WIJQJ33lS3hchDwceOxQf2CFLQ9w81WFZ4cpBWr5W4pukxwAX/1jj3aZDgBT/m2VfaEFAEL+eJqEqQIAMf6FnousAAEf/pGgoq4AAQ7+np+irwABBv+loJ+sAAIB/6qhnKcAAwD/rqKZoAADAP+woJOYAAQA/7GfjY4ABQD/sp2GhQAGAP+ym3l7AAcA/7KYbXIACAD/sZViZwAJAP+wkVZfAAoA/66NSmgADAD/q4c+YQAOAP+ogDBQABEA/6N4ITkAFAD/nnQRJwAYAP+XahgUABwA/5BhHQkAIAD/hmIjAgAkAP96bycAAikA/21xKgACLwD/YXUAADQA/1h5AQA4AP9PfgMAOwD/R4QBAEIA/0CEAABKAP87eQAAUAD/OGoAAFYA/zRXAABcAP8wRQAAYgD/LTEAAGIA/ykdAABiAP8lDQAAYgD/IwAAAGIA/yIAAABiAP8iAAAAYgA=";

// Session key to track if initial data has been loaded this session
const SESSION_INIT_KEY = 'algo_monitor_session_initialized';
const DAILY_LOAD_KEY = 'algo_signals_last_load_date';

const AlgoMonitor = ({ isAuthenticated, onAlertReceived }) => {
  const [alerts, setAlerts] = useState([]);
  const [showAlertPopup, setShowAlertPopup] = useState(false);
  const pollIntervalRef = useRef(null);
  const audioRef = useRef(null);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [newCells, setNewCells] = useState({}); // Track new cells for highlighting
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
  const initialLoadRef = useRef(false); // Track if first data fetch is complete
  const isFirstLoadTodayRef = useRef(true); // Track first load of the day

  // Initialize audio on mount and check if first load today
  useEffect(() => {
    try {
      audioRef.current = new Audio(NOTIFICATION_SOUND);
      audioRef.current.volume = 0.7;
    } catch (e) {
      console.error("Failed to initialize audio:", e);
    }
    
    // Check if this is first load of the day
    const today = new Date().toDateString();
    const lastLoadDate = localStorage.getItem(DAILY_LOAD_KEY);
    if (lastLoadDate !== today) {
      isFirstLoadTodayRef.current = true;
      localStorage.setItem(DAILY_LOAD_KEY, today);
    } else {
      isFirstLoadTodayRef.current = false;
    }
  }, []);

  // Play notification sound
  const playSound = useCallback(() => {
    if (soundEnabled && audioRef.current) {
      try {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(e => {
          console.log("Audio play failed (user interaction required):", e);
        });
      } catch (e) {
        console.error("Sound play error:", e);
      }
    }
  }, [soundEnabled]);

  // Load previous data from localStorage
  const loadPreviousData = useCallback((sheetIndex) => {
    const key = `algo_data_${sheetIndex}`;
    try {
      const stored = localStorage.getItem(key);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      console.error("Failed to load previous data:", e);
    }
    return null;
  }, []);

  // Save current data to localStorage (with row count for new row detection)
  const saveCurrentData = useCallback((sheetIndex, data) => {
    const key = `algo_data_${sheetIndex}`;
    try {
      localStorage.setItem(key, JSON.stringify({
        headers: data.headers,
        data: data.data,
        rowCount: data.data.length, // Important: track row count for new row detection
        timestamp: Date.now()
      }));
    } catch (e) {
      console.error("Failed to save current data:", e);
    }
  }, []);

  // Compare data and find NEW ROWS only (not cell changes)
  const findChangedCells = useCallback((sheetIndex, newData) => {
    const previousData = loadPreviousData(sheetIndex);
    const changedCells = [];
    
    // CRITICAL: On first load of the day, save data but don't alert
    if (isFirstLoadTodayRef.current) {
      console.log('AlgoMonitor: First load of the day - saving baseline, no alerts');
      saveCurrentData(sheetIndex, newData);
      isFirstLoadTodayRef.current = false;
      return { changedCells: [], isFirstLoad: true };
    }
    
    // CRITICAL: On first fetch after login, save data but don't alert
    if (!initialLoadRef.current) {
      saveCurrentData(sheetIndex, newData);
      return { changedCells: [], isFirstLoad: true };
    }
    
    if (!previousData || !previousData.data) {
      // No previous data exists - save and don't alert
      saveCurrentData(sheetIndex, newData);
      return { changedCells: [], isFirstLoad: true };
    }

    // ONLY check for NEW ROWS (not cell changes in existing rows)
    const previousRowCount = previousData.rowCount || previousData.data.length;
    
    // Get hidden columns from the sheet data
    const hiddenCols = newData.hidden_columns || [];
    
    if (newData.data.length > previousRowCount) {
      console.log(`AlgoMonitor: Detected ${newData.data.length - previousRowCount} new row(s) in sheet ${sheetIndex}`);
      for (let i = previousRowCount; i < newData.data.length; i++) {
        newData.data[i].forEach((cell, colIdx) => {
          // Skip hidden columns - only alert for visible columns
          if (hiddenCols.includes(colIdx)) {
            return;
          }
          if (cell && cell.trim()) {
            changedCells.push({
              rowIdx: i,
              colIdx,
              header: newData.headers[colIdx] || `Column ${colIdx + 1}`,
              value: cell,
              isNewRow: true
            });
          }
        });
      }
    }

    // Save current data for next comparison (with row count)
    saveCurrentData(sheetIndex, newData);

    return { changedCells, isFirstLoad: false };
  }, [loadPreviousData, saveCurrentData]);

  // Check for changes in all sheets
  const checkForUpdates = useCallback(async (isInitialFetch = false) => {
    if (!isAuthenticated) return;

    try {
      // Get all active sheets
      const sheetsResponse = await apiClient.get('/algo-setup/sheets');
      if (!sheetsResponse.data.success) return;

      const sheets = sheetsResponse.data.sheets || [];
      const allNewAlerts = [];
      const allNewCells = {};

      for (const sheet of sheets) {
        try {
          const dataResponse = await apiClient.get(`/algo-setup/data/${sheet.sheet_index}`);
          if (!dataResponse.data.success) continue;

          const { changedCells, isFirstLoad } = findChangedCells(sheet.sheet_index, dataResponse.data);
          
          // Only create alerts if NOT initial fetch and there are changes
          if (!isFirstLoad && !isInitialFetch && changedCells.length > 0) {
            // Track new cells for highlighting
            allNewCells[sheet.sheet_index] = changedCells.map(c => `${c.rowIdx}-${c.colIdx}`);
            
            // Create alerts for each changed cell
            changedCells.forEach(change => {
              allNewAlerts.push({
                id: `${Date.now()}-${change.rowIdx}-${change.colIdx}`,
                tabName: sheet.tab_name,
                header: change.header,
                value: change.value,
                prevValue: change.prevValue,
                isNewRow: change.isNewRow,
                timestamp: new Date().toISOString()
              });
            });
          }
        } catch (err) {
          console.error(`Error checking sheet ${sheet.sheet_index}:`, err);
        }
      }

      // Mark initial load as complete after first fetch
      if (isInitialFetch) {
        initialLoadRef.current = true;
        setIsInitialLoadComplete(true);
        console.log('AI Signals: Initial data load complete. Alerts will now trigger for new changes.');
        return;
      }

      if (allNewAlerts.length > 0) {
        // Update alerts
        setAlerts(prev => [...allNewAlerts, ...prev].slice(0, 50));
        setShowAlertPopup(true);
        setNewCells(allNewCells);
        
        // Play sound
        playSound();
        
        // Show toast notifications
        allNewAlerts.forEach(alert => {
          toast.success(
            `New Row: ${alert.header.toUpperCase()}`,
            { 
              duration: 8000,
              description: alert.value,
              icon: '🚨'
            }
          );
        });

        // Notify parent component
        if (onAlertReceived) {
          onAlertReceived(allNewAlerts, allNewCells);
        }
      }
    } catch (err) {
      console.error("Error in checkForUpdates:", err);
    }
  }, [isAuthenticated, findChangedCells, playSound, onAlertReceived]);

  // Start polling when authenticated
  useEffect(() => {
    if (!isAuthenticated) return;

    // Reset initial load flag when user logs in
    initialLoadRef.current = false;
    setIsInitialLoadComplete(false);

    // Initial fetch - load data without alerting (pass true for isInitialFetch)
    const initialTimeout = setTimeout(() => {
      checkForUpdates(true); // true = initial fetch, don't alert
    }, 3000);

    // Then poll every 30 seconds - these will trigger alerts for changes
    pollIntervalRef.current = setInterval(() => {
      checkForUpdates(false); // false = subsequent fetch, alert for changes
    }, 30000);

    return () => {
      clearTimeout(initialTimeout);
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [isAuthenticated, checkForUpdates]);

  // Clear alert highlight after some time
  useEffect(() => {
    if (Object.keys(newCells).length > 0) {
      const timeout = setTimeout(() => {
        setNewCells({});
      }, 60000); // Clear highlights after 1 minute
      return () => clearTimeout(timeout);
    }
  }, [newCells]);

  if (!showAlertPopup || alerts.length === 0) return null;

  return (
    <div className="fixed top-20 right-4 w-96 bg-[#111827] border border-red-500/50 rounded-xl shadow-2xl z-[100] overflow-hidden animate-in slide-in-from-top-5">
      <div className="flex items-center justify-between p-3 border-b border-[#1a2540] bg-gradient-to-r from-red-500/20 to-orange-500/20">
        <h3 className="text-sm font-bold text-red-400 flex items-center gap-2">
          <Bell className="w-4 h-4 animate-pulse" />
          New Updates ({alerts.length})
        </h3>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setSoundEnabled(!soundEnabled)}
            className={`p-1.5 rounded ${soundEnabled ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}
            title={soundEnabled ? 'Sound ON' : 'Sound OFF'}
          >
            🔊
          </button>
          <button 
            onClick={() => setShowAlertPopup(false)} 
            className="text-[#6b7a99] hover:text-white p-1"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div className="max-h-80 overflow-y-auto p-2 space-y-2">
        {alerts.slice(0, 10).map((alert, idx) => (
          <div 
            key={alert.id || idx} 
            className="p-3 bg-[#0a0e17] border border-green-500/30 rounded-lg"
            style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)' }}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm font-bold text-green-400">
                  {alert.header}
                </p>
                <p className="text-white text-sm mt-1">{alert.value}</p>
                {alert.prevValue && (
                  <p className="text-[10px] text-[#6b7a99] mt-1">
                    Previous: <span className="text-red-400 line-through">{alert.prevValue}</span>
                  </p>
                )}
              </div>
              <span className="text-[9px] text-[#6b7a99]">
                {new Date(alert.timestamp).toLocaleTimeString()}
              </span>
            </div>
          </div>
        ))}
      </div>
      <div className="p-2 border-t border-[#1a2540]">
        <Button
          onClick={() => { setAlerts([]); setShowAlertPopup(false); }}
          variant="outline"
          size="sm"
          className="w-full text-xs"
        >
          Clear All
        </Button>
      </div>
    </div>
  );
};

export default AlgoMonitor;
